import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Plus, Trash2, Loader2 } from 'lucide-react'
import { useUpdateDocumentMetadata } from '@/hooks/useDocuments'
import { useToast } from '@/hooks/use-toast'

interface MetadataDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  kbId: string
  ragflowDocumentId: string
  documentName: string
  initialMetadata?: Record<string, any>
}

interface MetadataEntry {
  id: string
  key: string
  value: string
}

export function MetadataDialog({
  open,
  onOpenChange,
  kbId,
  ragflowDocumentId,
  documentName,
  initialMetadata = {},
}: MetadataDialogProps) {
  const { toast } = useToast()
  const updateMetadata = useUpdateDocumentMetadata(kbId)
  
  const [entries, setEntries] = useState<MetadataEntry[]>([])
  const [initialized, setInitialized] = useState(false)

  // Initialize entries from initial metadata only when dialog opens
  useEffect(() => {
    if (open && !initialized) {
      const metadataEntries = Object.entries(initialMetadata).map(([key, value], index) => ({
        id: `${index}`,
        key,
        value: String(value),
      }))
      
      // If no metadata, start with one empty entry
      if (metadataEntries.length === 0) {
        setEntries([{ id: '0', key: '', value: '' }])
      } else {
        setEntries(metadataEntries)
      }
      setInitialized(true)
    } else if (!open) {
      // Reset initialization flag when dialog closes
      setInitialized(false)
    }
  }, [open, initialMetadata, initialized])

  const handleAddEntry = () => {
    const newId = String(Date.now())
    setEntries([...entries, { id: newId, key: '', value: '' }])
  }

  const handleRemoveEntry = (id: string) => {
    setEntries(entries.filter(entry => entry.id !== id))
  }

  const handleKeyChange = (id: string, key: string) => {
    setEntries(entries.map(entry =>
      entry.id === id ? { ...entry, key } : entry
    ))
  }

  const handleValueChange = (id: string, value: string) => {
    setEntries(entries.map(entry =>
      entry.id === id ? { ...entry, value } : entry
    ))
  }

  const handleSave = async () => {
    // Convert entries to metadata object, filtering out empty entries
    const metadata: Record<string, any> = {}
    entries.forEach(entry => {
      if (entry.key.trim()) {
        metadata[entry.key.trim()] = entry.value
      }
    })

    try {
      await updateMetadata.mutateAsync({
        ragflowDocumentId,
        metaFields: metadata,
      })

      toast({
        title: 'Metadata updated',
        description: 'Document metadata has been saved successfully',
      })

      onOpenChange(false)
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to update metadata',
        variant: 'destructive',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Document Metadata</DialogTitle>
          <DialogDescription>
            Add metadata to <span className="font-semibold">{documentName}</span>. 
            This information will be sent to the LLM with retrieved chunks.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2 space-y-3">
          {entries.map((entry) => (
            <div key={entry.id} className="flex gap-2 items-start">
              <div className="flex-1 space-y-1">
                <Label htmlFor={`key-${entry.id}`} className="text-xs">
                  Key
                </Label>
                <Input
                  id={`key-${entry.id}`}
                  placeholder="e.g., url, author, date"
                  value={entry.key}
                  onChange={(e) => handleKeyChange(entry.id, e.target.value)}
                />
              </div>
              <div className="flex-1 space-y-1">
                <Label htmlFor={`value-${entry.id}`} className="text-xs">
                  Value
                </Label>
                <Input
                  id={`value-${entry.id}`}
                  placeholder="Enter value"
                  value={entry.value}
                  onChange={(e) => handleValueChange(entry.id, e.target.value)}
                />
              </div>
              <div className="flex items-end h-full pt-6">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveEntry(entry.id)}
                  disabled={entries.length === 1}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddEntry}
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Field
          </Button>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateMetadata.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={updateMetadata.isPending}
          >
            {updateMetadata.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Metadata'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
